import { useState, useEffect, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { formatCurrency, formatUSD, getUsdRate } from "@vaulzo/core/constants";
import NetWorthChart from "./NetWorthChart";

export const TIMEFRAMES = [
  { value: "1m", label: "1M" },
  { value: "3m", label: "3M" },
  { value: "6m", label: "6M" },
  { value: "1y", label: "1Y" },
  { value: "all", label: "All time" },
];

export const RANGE_LABEL = {
  "1y": "in the last year",
  "6m": "in the last 6 months",
  "3m": "in the last 3 months",
  "1m": "in the last month",
};

/**
 * Hero number, period switch and the net-worth chart. Hovering the chart
 * scrubs the hero: it shows that day's net worth and the change from the
 * start of the period up to that day.
 */
export default function NetWorthCard({
  netWorth,
  snapshots = [],
  dateRange,
  onDateRangeChange,
  exchangeRates,
  isSnapshotsLoading = false,
  // The history request failed and nothing is cached for this range.
  historyFailed = false,
  onRetryHistory,
}) {
  const [hover, setHover] = useState(null);
  useEffect(() => setHover(null), [dateRange]);

  const points = useMemo(
    () => snapshots.map((s) => ({ date: s.date, value: s.net_worth })),
    [snapshots]
  );

  const first = points[0]?.value ?? netWorth;
  const scrubbed = hover != null ? points[hover] : null;
  const shownValue = scrubbed ? scrubbed.value : netWorth;
  const change = shownValue - first;
  const changePercent = first ? Math.abs((change / first) * 100).toFixed(1) : "0.0";
  const isPositive = change >= 0;

  const startDate = points[0] ? parseISO(points[0].date) : null;
  const changeLabel = scrubbed
    ? `since ${format(startDate, "d MMM yyyy")}`
    : RANGE_LABEL[dateRange] || (startDate ? `since ${format(startDate, "MMM yyyy")}` : "");

  const asOf = scrubbed
    ? format(parseISO(scrubbed.date), "d MMM yyyy")
    : points.length
      ? format(parseISO(points[points.length - 1].date), "d MMM yyyy")
      : format(new Date(), "d MMM yyyy");

  const usdValue = shownValue / getUsdRate(exchangeRates);

  return (
    <div className="animate-vz-fade">
      {/* Hero */}
      <div className="mb-10 text-center">
        <div className="mb-5 text-[12px] uppercase tracking-[0.14em] text-vaulzo-fainter">
          Net worth
        </div>
        {/* Scales with the viewport on phones; caps at the desktop 72px */}
        <div className="num text-[clamp(40px,13vw,72px)] font-bold leading-[0.92] tracking-[-0.045em] text-vaulzo-ink">
          {formatCurrency(shownValue)}
        </div>
        <div className="num mt-[10px] text-sm font-medium text-vaulzo-fainter">
          ≈ {formatUSD(usdValue, { suffix: "" })}
        </div>
        {points.length > 1 && (
          <div
            className="num mt-4 text-[15px] font-semibold"
            style={{ color: isPositive ? "rgb(var(--vz-accent))" : "rgb(var(--vz-danger))" }}
          >
            {isPositive ? "↑" : "↓"} {formatCurrency(Math.abs(change))} · {changePercent}% {changeLabel}
          </div>
        )}
        <div className="mt-[6px] text-[12px] text-vaulzo-subtle">as of {asOf}</div>
      </div>

      {/* Timeframe segmented control */}
      <div className="mb-[30px] flex justify-center">
        <div className="inline-flex gap-[2px] rounded-[11px] bg-vaulzo-row p-1">
          {TIMEFRAMES.map((t) => {
            const active = dateRange === t.value;
            return (
              <button
                key={t.value}
                onClick={() => onDateRangeChange(t.value)}
                disabled={isSnapshotsLoading}
                className={`rounded-lg px-[15px] py-[7px] text-[13px] transition-all disabled:opacity-60 ${
                  active
                    ? "bg-vaulzo-surface font-semibold text-vaulzo-ink shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                    : "bg-transparent font-medium text-vaulzo-faint hover:text-vaulzo-ink"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div className="relative mb-14">
        {points.length === 0 && historyFailed ? (
          // A failed request is not "no assets yet": say so and offer a retry.
          <div className="flex h-[200px] flex-col items-center justify-center rounded-lg bg-vaulzo-row/60 px-6 text-center">
            <div className="text-[15px] font-semibold text-vaulzo-ink">Couldn't load your history</div>
            <div className="mt-1 text-[13px] text-vaulzo-faint">
              The server didn't answer. Your data is safe; try again in a moment.
            </div>
            <button
              type="button"
              onClick={onRetryHistory}
              disabled={isSnapshotsLoading}
              className="mt-4 rounded-[10px] bg-vaulzo-accent px-[18px] py-[9px] text-sm font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60"
            >
              {isSnapshotsLoading ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : points.length === 0 ? (
          <div className="flex h-[200px] items-center justify-center rounded-lg bg-vaulzo-row/60 text-sm text-vaulzo-subtle">
            Add assets to see your net worth over time
          </div>
        ) : (
          <>
            <NetWorthChart points={points} hoverIndex={hover} onHover={setHover} />
            {isSnapshotsLoading && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-vaulzo-accent" aria-hidden />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
